import { lazy, Suspense, useEffect } from "react"

import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import SuspenseLoader from "./app_variants/SuspenseLoader"
import { useLocalStorage } from "./hooks/useLocalStorage"
import { useMobileAppInterface } from "./hooks/useMobileAppInterface"
import {
  changeAppLanguage,
  DEFAULT_LANGUAGE,
  getSupportedLanguage,
  LANGUAGE_STORAGE_KEY,
  MOBILE_LANGUAGES,
  WEB_LANGUAGES,
} from "./i18n"
import { VISITOR_BASE_URL } from "./screens/Visitors/constants"
import { getDeviceLanguage } from "./utils"
import { Capacitor } from "@capacitor/core"
import { SplashScreen } from "@capacitor/splash-screen"

import { changeLanguage } from "./redux/app/appSlice"
import { selectIsMobile } from "./redux/app/selectors"
import { useAppSelector } from "./redux/reducers"
import { useActions } from "./redux/utils"

import "./App.scss"

const DesktopApp = lazy(() => import("./app_variants/DesktopApp"))
const OpenPathApp = lazy(() => import("./app_variants/OpenPathApp"))
const VisitorManagementApp = lazy(
  () => import("./app_variants/VisitorManagementApp"),
)

export const isOpenPath = () => {
  const path = window.location.pathname
  return (
    path.startsWith("/visitor-login") ||
    path.startsWith("/device/shareable/") ||
    (path.startsWith("/health-screening") && !path.includes("employee"))
  )
}

export const AppVariant = () => {
  const isMobile = useAppSelector(selectIsMobile)
  const isTablet = useAppSelector((state) => state.app.isTablet)
  const visitorsPath = window.location.pathname.startsWith(VISITOR_BASE_URL)
  const { isNewMobileAppActive: isNewMobileEnabled } = useMobileAppInterface()

  const actions = useActions({
    changeLanguage: (lang: string) => changeLanguage(lang),
  })

  const { i18n } = useTranslation()
  const { value: storedLanguage } = useLocalStorage(
    LANGUAGE_STORAGE_KEY,
    DEFAULT_LANGUAGE,
  )

  /**
   * JOAN-10735: hide the native splash screen as soon as React has
   * committed its first render. Pairs with `launchAutoHide: false` in
   * `capacitor.config.ts` — the native side has no auto-hide timer, so
   * the splash stays visible until this call runs. This both ties the
   * splash duration to actual app readiness and silences the iOS
   * "automatically hidden after default timeout" warning, which is only
   * emitted from the (now-disabled) auto-hide code path.
   */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    SplashScreen.hide().catch(() => {})
  }, [])

  useEffect(() => {
    const initializeLanguage = storedLanguage || DEFAULT_LANGUAGE

    if (isMobile) {
      const syncMobileLanguage = async () => {
        /**
         * The old mobile app has no in-app language picker, so it follows the
         * device locale on every launch. The new mobile app has its own picker
         * and persists the choice in `storedLanguage`.
         */
        const desiredLanguage = isNewMobileEnabled
          ? initializeLanguage
          : await getDeviceLanguage()

        const supportedLanguages = isNewMobileEnabled
          ? MOBILE_LANGUAGES
          : WEB_LANGUAGES
        const supportedLanguage = getSupportedLanguage(
          desiredLanguage,
          supportedLanguages,
        )

        if (i18n.language !== supportedLanguage) {
          await changeAppLanguage(supportedLanguage)
          actions.changeLanguage(supportedLanguage)
        }

        dayjs.locale(supportedLanguage)
      }

      void syncMobileLanguage()
      return
    }

    const initLanguage = async () => {
      const supportedLanguage = getSupportedLanguage(
        initializeLanguage,
        WEB_LANGUAGES,
      )

      if (i18n.language !== supportedLanguage) {
        await changeAppLanguage(supportedLanguage)
        actions.changeLanguage(supportedLanguage)
      }
    }

    initLanguage()
  }, [i18n, storedLanguage, actions, isMobile, isNewMobileEnabled])

  return (
    <Suspense fallback={<SuspenseLoader />}>
      {isOpenPath() ? (
        <OpenPathApp />
      ) : isTablet || visitorsPath ? (
        <VisitorManagementApp />
      ) : (
        <DesktopApp />
      )}
    </Suspense>
  )
}

export default AppVariant
